import * as React from 'react';

import Enzymes from 'molbio/enzymes/backbone/Enzymes';
import {NO_ENZYME_SENTINEL} from 'molbio/enzymes/EnzymeConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface RestrictionEnzymeDropdownProps {
  allowedRestrictionEnzymeIds: Array<number>;
  isDisabled: boolean;
  restrictionEnzymeId: number;
  shouldAllowNull: boolean;
  updateRestrictionEnzymeId: (restrictionEnzymeId: number) => void;
}

const COMMON_ENZYMES = Enzymes.getCommonEnzymes();

/**
 * A dropdown to choose a restriction enzyme.
 *
 * @param props Component props
 * @param {Array<number>} props.allowedRestrictionEnzymeIds List of database IDs of enzymes to show
 * @param {boolean} props.isDisabled Whether or not the input should be disabled.
 * @param {number} props.restrictionEnzymeId The database ID of the restriction enzyme to show as selected in the dropdown.
 * @param {boolean} props.shouldAllowNull Whether or not a null enzyme should be allowed.
 * @param {function} props.updateRestrictionEnzymeId A function that takes a new restriction enzyme ID and uses it to update state.
 * @constructor
 */
export function RestrictionEnzymeDropdown(props: RestrictionEnzymeDropdownProps): JSX.Element {
  return (
    <SelectDropdown
      id="restrictionEnzyme"
      displayName={
        props.restrictionEnzymeId === NO_ENZYME_SENTINEL
          ? 'None selected'
          : COMMON_ENZYMES.get(props.restrictionEnzymeId).name()
      }
      disabled={props.isDisabled}
      menu={
        <SearchableMenu
          selectedValue={props.restrictionEnzymeId}
          width={MenuWidth.FULL}
          optionDatas={[
            ...(props.shouldAllowNull ? [{name: 'None', value: NO_ENZYME_SENTINEL}] : []),
            ...props.allowedRestrictionEnzymeIds.map((id) => ({
              name: COMMON_ENZYMES.get(id).name(),
              value: id,
            })),
          ]}
          onSelect={(restrictionEnzymeId) => {
            props.updateRestrictionEnzymeId(restrictionEnzymeId);
          }}
        />
      }
      dataTestAttrs={{
        component: 'RestrictionEnzymeDropdown',
        element: 'selectDropdown',
      }}
    />
  );
}
